import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../components/BackButton";
import api from "../../api/api";
import "./performainvoice.css";
import logo from "../../image/logo.png";

const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatNumber = (value) =>
  numberValue(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "NA";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, "0");
  const monthNames = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  return `${day}-${monthNames[date.getMonth()]}-${date.getFullYear()}`;
};

const labelValue = (value) =>
  typeof value === "object" && value ? value.label || value.value || "" : value || "";

// Freight terms is a fixed dropdown on Taxinvoice.js, but older drafts stored
// free text, so compare case-insensitively.
const isCollectTerms = (value) => String(value || "").trim().toLowerCase() === "collect";

// A charge keeps a stable identity from the draft (row_key) so the Collect
// split survives re-renders. Drafts written before row_key existed have no
// key, so fall back to the charge's position.
const chargeKey = (charge, index) => charge?.row_key || `charge_${index}`;

// Sum a subset of charges. Each invoice in a Collect pair must be totalled on
// its own charges only, never across the whole set.
const totalsForCharges = (list = []) =>
  list.reduce(
    (acc, charge) => {
      acc.subTotal += numberValue(charge.amount_in_inr);
      acc.gstTotal += numberValue(charge.gst_amount);
      return acc;
    },
    { subTotal: 0, gstTotal: 0 }
  );

const chargeCodeLabel = (charge) => {
  if (!charge) return "";
  if (charge.charge_code) {
    return labelValue(charge.charge_code) || charge.charge_name || charge.charge_code;
  }
  return charge.charge_name || "";
};

const currencySymbol = (currency) => {
  if (!currency || currency === "INR") return "₹";
  return `${currency} `;
};
export const termsandcondition = [
  "All payments are strictly to be made in name of GFR Logistics India Pvt. Ltd. only, with below banking details.",
  "Payment made after expiry of agreed payment schedule, shall be liable to simple interest @ 24% per annum.",
  "Disputes raised by any party, If any, shall be resolved mutually within one month from date of invoice.",
  "Disputes if any not raised within seven days of receipt of invoice shall be deemed as that no dispute exists.",
  "Disputes if any shall be subject to the jurisdiction of Delhi courts only.",
];
const CreatePerformaInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [selectedIncome, setSelectedIncome] = useState();
  const [showIncomeModal, setShowIncomeModal] = useState();
  const [showSuccessModal, setShowSuccessModal] = useState();

  useEffect(() => {
    try {
      const savedDraft = sessionStorage.getItem(`sales_invoice_draft_${id}`);
      setDraft(savedDraft ? JSON.parse(savedDraft) : null);
    } catch (err) {
      console.error("Failed to read invoice preview data:", err);
    }
  }, [id]);

  const invoice = draft || {};
  // console.log("invoiceinvoice",invoice)
  const isEditing = Boolean(invoice.sale_invoice_id);
  const charges = Array.isArray(invoice.charges) ? invoice.charges : [];
  const containers = Array.isArray(invoice.containers) ? invoice.containers : [];
  const editedExchangeRates = invoice.editedExchangeRates || {};

  // New format: invoices array from Taxinvoice.js
  const invoiceSplit = Array.isArray(invoice.invoices) ? invoice.invoices : [];

  // Backward compatibility: old format with consignee_charge_keys
  const consigneeChargeKeys = Array.isArray(invoice.consignee_charge_keys)
    ? invoice.consignee_charge_keys
    : [];

  const hasSplit = invoiceSplit.length >= 2 || (
    String(invoice.freight_terms || "").trim().toLowerCase() === "collect" &&
    consigneeChargeKeys.length > 0
  );
  const invoice2BillingParty = invoice.invoice2_billing_party || "Consignee";

  const consigneeCharges = charges.filter((c, i) =>
    consigneeChargeKeys.includes(chargeKey(c, i))
  );
  const shipperCharges = charges.filter(
    (c, i) => !consigneeChargeKeys.includes(chargeKey(c, i))
  );

  const previewInvoices = invoiceSplit.length > 0
    ? invoiceSplit
    : hasSplit
      ? [
        {
          id: "inv1",
          billing_party: invoice.billing_party,
          currency: "INR",
          charge_keys: [],
        },
        {
          id: "inv2",
          billing_party: invoice2BillingParty,
          currency: "USD",
          charge_keys: consigneeChargeKeys,
        },
      ]
      : [
        {
          id: "inv1",
          billing_party: invoice.billing_party,
          currency: "INR",
          charge_keys: [],
        },
      ];

  const getChargesForKeys = (keys = []) => {
    if (keys.length === 0) return charges;
    return keys
      .map((key) => charges.find((c) => c.row_key === key))
      .filter(Boolean);
  };

  const consigneeName = invoice.consignee_name || invoice.consignee || "Not provided";
  const shipperName = invoice.shipper_name || invoice.shipper || "Not provided";
  const notifyName =
    invoice.notify_party_name || invoice.notify_party1 || invoice.notify_party || "Same As Consignee";
  const notifyAddress =
    invoice.notify_party1_address || invoice.notify_party_address || "";

  const notifyParty2 =
    invoice.notify_party2_name ||
    invoice.notify_party2 ||
    invoice.notify_party2_address ||
    "";

  const notifyParty2Address = invoice.notify_party2_address || "";

  const shipmentSegment =
    invoice.shipment_segment?.value ||
    invoice.shipment_segment ||
    "";
  const containerText = containers
    .map((c) => [c.container_number, c.container_type].filter(Boolean).join(" / "))
    .filter(Boolean)
    .join(", ");
  const grossWeight = invoice.gross_weight ? `${numberValue(invoice.gross_weight)} KGS` : "NA";

  function numberToWords(num) {
    const ones = [
      "", "one", "two", "three", "four", "five",
      "six", "seven", "eight", "nine", "ten",
      "eleven", "twelve", "thirteen", "fourteen",
      "fifteen", "sixteen", "seventeen", "eighteen",
      "nineteen"
    ];

    const tens = [
      "", "", "twenty", "thirty", "forty",
      "fifty", "sixty", "seventy", "eighty", "ninety"
    ];

    function convert(n) {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
      if (n < 1000) return ones[Math.floor(n / 100)] + " hundred" + (n % 100 ? " " + convert(n % 100) : "");
      if (n < 100000) return convert(Math.floor(n / 1000)) + " thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
      if (n < 10000000) return convert(Math.floor(n / 100000)) + " lakh" + (n % 100000 ? " " + convert(n % 100000) : "");
      return convert(Math.floor(n / 10000000)) + " crore" + (n % 10000000 ? " " + convert(n % 10000000) : "");
    }

    return convert(Math.floor(num));
  }

  // const amountWords = `INR ${formatNumber(grandTotal)} ONLY`;

  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  // Collect (freight terms = Collect) splits the charges across two invoices.
  // The split is decided on Taxinvoice.js and arrives in the draft, so this
  // page only renders it. `activeTab` selects which invoice is on screen.
  const [activeTab, setActiveTab] = useState(previewInvoices[0]?.id || "inv1");

  // The invoice currently on screen. For Prepaid this is always invoice 1
  // with every charge. Declared after `activeTab` so it is never read before
  // initialisation.
  const activeInvoice = previewInvoices.find(inv => inv.id === activeTab) || previewInvoices[0];
  const activeCharges = getChargesForKeys(activeInvoice?.charge_keys);
  const activeBillingParty = activeInvoice?.billing_party || invoice.billing_party;
  const activeInvNumber = activeInvoice?.invoice_number || (activeInvoice?.id === previewInvoices[1]?.id ? `${invoice.inv_number}-consg` : invoice.inv_number);

  // Totals follow the active tab. For Prepaid, shipperCharges is every charge
  // and these match the draft's stored whole-set totals exactly.
  const { subTotal: rawSubTotal, gstTotal: rawGstTotal } = previewInvoices.length > 1
    ? totalsForCharges(activeCharges)
    : {
      subTotal: numberValue(invoice.sub_total),
      gstTotal: numberValue(invoice.gst_total),
    };

  const placeOfSupply = (invoice.job_number || "").slice(3, 5) || "";
  const isInterState = placeOfSupply && placeOfSupply !== "07";
  const roundOff = numberValue(invoice.round_off);

  const activeCurrency = activeInvoice?.currency || "INR";
  const isINR = activeCurrency === "INR";
  const currencySym = isINR ? "₹" : activeCurrency === "USD" ? "$" : activeCurrency === "EUR" ? "€" : "£";

  // Get exchange rate for INR → invoice currency conversion from editedExchangeRates (saved in draft)
  // Falls back to first charge's exchange_rate if not in editedExchangeRates
  const activeExchangeRate = !isINR
    ? Number(editedExchangeRates[activeCurrency] || activeCharges[0]?.exchange_rate || 1)
    : 1;

  // For INR invoices: collect unique non-INR currencies from charges and their exchange rates
  const chargeExchangeRates = isINR
    ? Array.from(
        new Map(
          activeCharges
            .filter((c) => c.currency && c.currency !== "INR" && c.exchange_rate)
            .map((c) => [c.currency, Number(editedExchangeRates[c.currency] || c.exchange_rate || 1)])
        ).entries()
      )
    : [];

  // Convert to invoice currency for display (INR base → invoice currency)
  // exchangeRate = INR per 1 unit of foreign currency (e.g., 83 INR per 1 USD)
  // So: amount_in_invoice_currency = amount_in_inr / exchangeRate
  const subTotal = !isINR && activeExchangeRate > 0
    ? Number((rawSubTotal / activeExchangeRate).toFixed(2))
    : rawSubTotal;
  // GST only applies to INR invoices
  const gstTotal = isINR ? rawGstTotal : 0;
  const grandTotal = subTotal + gstTotal + roundOff;

  // Amount in words follows the active tab's grand total (in invoice currency).
  const amountWords = isINR
    ? `INR ${numberToWords(grandTotal).toUpperCase()} ONLY`
    : `${activeCurrency} ${numberToWords(grandTotal).toUpperCase()} ONLY`;

  // Resolve against the active invoice, so invoice 2 shows its own billing
  // party rather than invoice 1's.
  const billingPartyValue = labelValue(activeBillingParty);

  // `billing_party` is only the selected LABEL, so the name and address have to
  // be resolved from the matching party field. Reading a `billing_party_address`
  // key that is never populated is what produced the hardcoded placeholder block.
  const BILLING_PARTY_SOURCES = {
    shipper: { name: () => invoice.shipper, address: () => invoice.shipper_address },
    consignee: { name: () => invoice.consignee, address: () => invoice.consignee_address },
    "notify party": { name: () => invoice.notify_party1, address: () => invoice.notify_party1_address },
    "notify party 2": {
      name: () => activeInvoice?.notify_party2 || invoice.notify_party2,
      address: () => activeInvoice?.notify_party2_address || invoice.notify_party2_address
    },
  };
  const billingPartySource = BILLING_PARTY_SOURCES[
    String(billingPartyValue).trim().toLowerCase()
  ];
  const billingParty = billingPartySource
    ? billingPartySource.name() || "Not provided"
    : invoice.billing_party_name || "Not provided";
  const billingPartyAddress = billingPartySource ? billingPartySource.address() || "" : "";

  // Notify Party 2 is only a real column on this invoice when the user picked
  // it as the billing party. The form collects notify_party2 for every invoice
  // (the enquiry may carry one), so presence alone is not enough to render it.
  const hasNotifyParty2 =
    String(billingPartyValue).trim().toLowerCase() === "notify party 2" &&
    Boolean(activeInvoice?.notify_party2 || invoice.notify_party2);

  // Build the create/update payload for ONE invoice. Called once for Prepaid
  // and twice for Collect (once per invoice), each with its own charge subset,
  // billing party, invoice number and totals.
  const buildInvoicePayload = ({ chargeList, billingParty, invNumber, currency, invoiceDate }) => {
    // Invoice exchange rate from editedExchangeRates (INR per 1 invoice currency)
    const invoiceExchangeRate = currency !== "INR"
      ? Number(editedExchangeRates[currency]) || 1
      : 1;

    // Subtotal: sum of each charge converted to invoice currency via INR
    // Matches Taxinvoice.js display: charge.amount_in_inr / invoiceExchangeRate
    const subTotal = chargeList.reduce((acc, c) => {
      const chargeInInr = numberValue(c.amount_in_inr);
      if (currency === "INR") return acc + chargeInInr;
      return acc + (invoiceExchangeRate > 0 ? chargeInInr / invoiceExchangeRate : 0);
    }, 0);

    // GST only for INR invoices
    const gstTotal = currency === "INR"
      ? chargeList.reduce((acc, c) =>
          (c.gst_option === "yes" || c.gst_option?.value === "yes")
            ? acc + numberValue(c.gst_amount)
            : acc, 0)
      : 0;

    const roundOff = numberValue(invoice.round_off);
    const grandTotal = subTotal + gstTotal + roundOff;

    return {
      enq_id: invoice.enq_id || id,
      invoice_type: "performa",
      job_number: invoice.job_number,
      lut_number: invoice.lut_number,
      inv_number: invNumber,
      invoice_date: invoiceDate || invoice.invoice_date,
      billing_party: billingParty,
      term_and_condition: invoice.term_and_condition,
      currency: currency || "INR",
      exchange_rate: currency !== "INR" ? invoiceExchangeRate : null,
      sub_total: Number(subTotal.toFixed(2)),
      gst_total: Number(gstTotal.toFixed(2)),
      grand_total: Number(grandTotal.toFixed(2)),
      payment_ref: invoice.payment_ref,
      payment_date: invoice.payment_date,
      charges: chargeList.map((c) => {
        // Calculate amount in invoice currency: amount_in_inr / invoice_exchange_rate
        const amountInInvoiceCurrency = currency !== "INR" && invoiceExchangeRate > 0
          ? Number((numberValue(c.amount_in_inr) / invoiceExchangeRate).toFixed(2))
          : numberValue(c.amount_in_inr);

        // Apply GST rules: only INR currency with gst_option = "yes" can have GST
        const isINR = currency === "INR";
        const gstOptionValue = typeof c.gst_option === "object"
          ? c.gst_option?.value || "no"
          : c.gst_option || "no";
        const gstEnabled = isINR && gstOptionValue === "yes";
        const gstPercentage = gstEnabled ? numberValue(c.gst_percentage) : 0;
        const gstAmount = gstEnabled ? numberValue(c.gst_amount) : 0;

        return {
          charge_code: c.charge_code || "",
          sac_code: c.sac_code || "",
          currency: c.currency || "",
          exchange_rate: c.exchange_rate || 1,
          rate: c.rate || 0,
          quantity: c.quantity || 0,
          amount: c.amount || 0,
          amount_in_inr: c.amount_in_inr || 0,
          amount_in_invoice_currency: amountInInvoiceCurrency,
          gst_option: gstOptionValue,
          gst_percentage: gstPercentage,
          gst_amount: gstAmount,
        };
      }),
    };
  };

  // Persist one invoice and return its sale_invoice_id. `existingId` is
  // supplied only when editing an already-saved invoice.
  const saveInvoice = async (payload, existingId) => {
    if (existingId) {
      const updateRes = await api.put(`/sale-invoices/${existingId}`, payload);
      if (!updateRes.data?.success) {
        throw new Error(updateRes.data?.error || "Failed to update performa invoice.");
      }
      return updateRes.data.sale_invoice_id || existingId;
    }
    const createRes = await api.post("/sale-invoices/createPerforma", payload);
    if (!createRes.data?.success) {
      throw new Error(createRes.data?.error || "Failed to save performa invoice.");
    }
    return createRes.data.sale_invoice_id;
  };

  const handleCreatePerforma = async () => {
    if (saving) return;
    setSaving(true);
    setSaveMessage("");
    try {
      // Step 1: Save invoice data to DB (sale_invoices + charges only)
      // Shared fields (shipper, consignee, ports, vessel, etc.) live in enquiry table
      //
      // A Collect split (chosen on Taxinvoice.js, carried in the draft) raises
      // two invoices: invoice 1 keeps the charges NOT selected for the
      // consignee, invoice 2 gets the selected ones plus a "-consg" suffix.
      // Without a split this saves exactly one invoice, as before.
      const existingSaleInvoiceId = invoice.sale_invoice_id || null;

      let sale_invoice_id = null;
      let sale_invoice_id_2 = null;

      for (let i = 0; i < previewInvoices.length; i++) {
        const invData = previewInvoices[i];
        const chargeList = getChargesForKeys(invData.charge_keys);
        const isFirst = i === 0;
        const invNumber = invData.invoice_number || (i === 0 ? invoice.inv_number : `${invoice.inv_number}-consg`);

        const payload = buildInvoicePayload({
          chargeList,
          billingParty: invData.billing_party,
          invNumber,
          currency: invData.currency || "INR",
          invoiceDate: invData.invoice_date || invoice.invoice_date,
        });

        const savedId = await saveInvoice(
          payload,
          isEditing && isFirst ? existingSaleInvoiceId : null,
        );

        if (isFirst) {
          sale_invoice_id = savedId;
        } else {
          sale_invoice_id_2 = savedId;
        }
      }

      // Step 1.5: Sync shared fields + containers back to enquiry
      // (user may have edited shipper, consignee, ports, vessel, containers, etc.)
      const enquiryPayload = {
        first_shipper_name: invoice.shipper,
        first_shipper_address: invoice.shipper_address,
        first_consignee_name: invoice.consignee,
        first_consignee_address: invoice.consignee_address,
        notify_party_1: invoice.notify_party1,
        notify_party_1_address: invoice.notify_party1_address,
        notify_party_2: invoice.notify_party2,
        notify_party_2_address: invoice.notify_party2_address,
        pol: invoice.pol,
        pod: invoice.pod,
        place_of_receipt: invoice.place_of_receipt,
        final_port_of_delivery: invoice.final_port_of_delivery,
        transshipment_hub: invoice.transshipment_hub,
        shipment_segment: invoice.shipment_segment,
        incoterm: invoice.incoterms,
        hs_description: invoice.commodity_name,
        freight_terms: invoice.freight_terms,
        gross_weight: invoice.gross_weight,
        packaging_count: invoice.number_of_packages,
        container_number: invoice.container_number,
        stuffing_destuffing_At: invoice.consolidation_at,
        vessel_name: invoice.vessel_name,
        voyage_number: invoice.voyage_number,
        BL_id: invoice.bl_number,
        commercial_invoice_number: invoice.other_reference,
        credit_days: invoice.credit_days,
        credit_days_system: invoice.credit_days_system,
        routing: invoice.routing
      };

      try {
        await api.put(
          `/enquiry/updateEnquiry/${invoice.enq_id || id}`,
          enquiryPayload
        );
      } catch (enqErr) {
        setSaveMessage("Failed to sync enquiry: " + (enqErr?.response?.data?.error || enqErr?.message || "Unknown error"));
      }

      try {
        await api.put(
          `/enquiry/updateContainers/${invoice.enq_id || id}`,
          {
            containers: Array.isArray(invoice.containers)
              ? invoice.containers
              : []
          }
        );
      } catch (contErr) {
        console.error("Failed to sync containers:", contErr);
      }

      // Step 2: Generate PDF from the rendered invoice HTML and upload to Azure
      // Each invoice in a Collect pair gets its own render and its own blob.
      const invoiceEl = document.getElementById("invoice");
      const html = invoiceEl ? invoiceEl.outerHTML : "";

      const renderPdf = async (targetId, label) => {
        const genRes = await api.post("/sale-invoices/generate-proforma", {
          sale_invoice_id: targetId,
          enq_id: invoice.enq_id || id,
          html,
          blob_name: `PERFORMA_INVOICE_${invoice.enq_id || id}_${label}_${Date.now()}.pdf`,
        });
        if (!genRes.data?.success) {
          throw new Error(
            genRes.data?.error || "Failed to generate performa invoice PDF."
          );
        }
      };

      await renderPdf(sale_invoice_id, "INV1");
      if (sale_invoice_id_2) {
        await renderPdf(sale_invoice_id_2, "INV2");
      }

      setSaveMessage(
        sale_invoice_id_2
          ? `Two performa invoices created (${invoice.inv_number} and ${invoice.inv_number}-consg) with PDFs uploaded successfully.`
          : isEditing
            ? "Performa invoice updated and PDF regenerated successfully."
            : "Performa invoice created and PDF uploaded successfully."
      );
      // The draft is only needed to survive Back navigation from this
      // preview page. Once it is saved it is stale, and because the key is
      // per-enquiry it would otherwise prefill the next invoice created for
      // the same enquiry.
      try {
        sessionStorage.removeItem(`sales_invoice_draft_${id}`);
      } catch (e) {
        console.error("Failed to clear saved invoice draft:", e);
      }
      setShowSuccessModal(true);
    } catch (err) {
      console.error(
        isEditing ? "Error updating" : "Error creating",
        "performa invoice:",
        err
      );
      setSaveMessage(
        err?.message ||
        err?.response?.data?.error ||
        (isEditing
          ? "Failed to update performa invoice."
          : "Failed to create performa invoice.")
      );
    } finally {
      setSaving(false);
      setShowConfirm(false);
    }
  };

  // =========================================================
  // SUCCESS MODAL OK
  // =========================================================

  const handleSuccessOk = () => {
    setShowSuccessModal(false);
    navigate("/billingAndInvoice/invoicing");
  };

  // =========================================================
  // CLOSE SUCCESS MODAL
  // =========================================================

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      <BackButton />

      <div className="performa-invoice-page">
        {/* Action Buttons */}
        <div className="mb-3">
         
           
         

              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-primary"
                  // The Collect split was decided on Taxinvoice.js and arrives in the draft,
                  // so this page saves directly.
                  onClick={() => setShowConfirm(true)}
                  disabled={saving}
                >
                  {isEditing
                    ? "Update Performa Invoice"
                    : "Create Performa Invoice"}
                </button>
              </div>
           
        


          {/* A Collect split raises two invoices, so offer one button per invoice.
              Same control as ViewBLdraft.js's Switch BL BL1/BL2 tabs, and kept
              outside #invoice so it is not captured in the printed output. */}
          {previewInvoices.length > 1 && (
            <div style={{ marginBottom: "20px", display: "flex", gap: "10px" }}>
              {previewInvoices.map((inv, idx) => (
                <button
                  key={inv.id}
                  onClick={() => setActiveTab(inv.id)}
                  className={`btn ${activeTab === inv.id ? "btn-primary" : "btn-outline-primary"}`}
                  style={{ padding: "10px 20px" }}
                >
                  Invoice {idx + 1} ({inv.invoice_number || inv.inv_number || "Draft"})
                  {inv.billing_party && <span> - {inv.billing_party}</span>}
                </button>
              ))}
            </div>
          )}

          {/* Invoice */}
          <div id="invoice" className="invoice">
            <table className="table table-bordered invoice-table custom-invoice-table mb-0 table-layout-fixed">
              <tbody>
                <tr className="b-0">
                  <td
                    rowSpan="9"
                    colSpan="2"
                    className="text-center align-top b-0 p-0"
                  >
                    <div className="d-flex flex-column gap-2 mt-2">
                      <img
                        src={logo}
                        alt="logo"
                        className="pe-5 ps-3"
                        style={{ maxHeight: "90px" }}
                      />

                      <div className="bt d-flex" style={{ height: "150px" }}>
                        {/* QR code & IRN are added at final invoice stage */}
                      </div>
                    </div>
                  </td>

                  <td colSpan="6" className="text-center fw-bold fs-6 b-0 bl">
                    GFR Logistics India Private Limited
                  </td>
                </tr>

                <tr className="b-0">
                  <td
                    colSpan="1"
                    style={{ width: "25%", fontWeight: "bold" }}
                    className="br-0 bb-0"
                  >
                    Corporate Address :
                  </td>

                  <td colSpan="5" className="br-0 bb-0">
                    416,4th floor, Vishwadeep Tower, District Centre, Janakpuri,
                    New Delhi, India 110058
                  </td>
                </tr>

                <tr className="b-0">
                  <td
                    colSpan="1"
                    style={{ fontWeight: "bold" }}
                    className="br-0 bb-0"
                  >
                    LUT Number :
                  </td>

                  <td colSpan="5" className="br-0 bb-0">
                    {invoice.lut_number}
                  </td>
                </tr>

                <tr className="b-0">
                  <td
                    colSpan="1"
                    style={{ fontWeight: "bold" }}
                    className="br-0 bb-0"
                  >
                    Telephone :
                  </td>

                  <td colSpan="5" className="br-0 bb-0">
                    +91 95992 45573
                  </td>
                </tr>

                <tr className="b-0">
                  <td
                    colSpan="1"
                    style={{ fontWeight: "bold" }}
                    className="br-0 bb-0"
                  >
                    General Enquiries :
                  </td>

                  <td colSpan="5" className="br-0 bb-0">
                    info@gfrlogistics.com
                  </td>
                </tr>

                <tr className="b-0">
                  <td
                    colSpan="1"
                    style={{ fontWeight: "bold" }}
                    className="br-0 bb-0"
                  >
                    INVOICE NUMBER :
                  </td>

                  <td colSpan="5" className="br-0 bb-0">
                    {activeInvoice?.invoice_number || invoice.inv_number}
                  </td>
                </tr>

                <tr className="b-0">
                  <td
                    colSpan="1"
                    style={{ fontWeight: "bold" }}
                    className="br-0 bb-0"
                  >
                    Invoice Date :
                  </td>

                  <td colSpan="5" className="br-0 bb-0">
                    {formatDate(activeInvoice?.invoice_date || invoice.invoice_date) || "15-Oct-2025"}
                  </td>
                </tr>

                <tr className="b-0">
                  <td
                    colSpan="1"
                    style={{ fontWeight: "bold" }}
                    className="br-0 bb-0"
                  >
                    GST Number :
                  </td>

                  <td colSpan="5" className="br-0 bb-0">
                    07AAKCG3385M1Z1
                  </td>
                </tr>

                <tr className="b-0">
                  <td
                    colSpan="1"
                    style={{ fontWeight: "bold" }}
                    className="br-0 bb-0"
                  >
                    MSME Number :
                  </td>

                  <td colSpan="5" className="br-0 bb-0">
                    UDYAM-DL-11-0061053
                  </td>
                </tr>

                <tr className="b-0">
                  <td
                    className="text-center br-0 bb-0 bl-0"
                    colSpan="8"
                    style={{
                      height: "55px",
                      verticalAlign: "middle",
                    }}
                  >
                    <b className="fs-2 mnhnd">TAX INVOICE</b>
                  </td>
                </tr>

                {/* BILLING / CONSIGNEE */}

                <tr className="b-0">
                  <td colSpan="4" className="bl-0 bb-0">
                    <b>BILLING PARTY</b>
                  </td>

                  <td colSpan="4" className="br-0 bl-0 bb-0">
                    <b>CONSIGNEE</b>
                  </td>
                </tr>

                <tr className="b-0">
                  <td colSpan="4" className="bl-0 bb-0">
                    {billingParty}
                    <br />
                    {billingPartyAddress}
                  </td>

                  <td colSpan="4" className="br-0 bl-0 bb-0">
                    {consigneeName}
                    <br />
                    {invoice.consignee_address}
                  </td>
                </tr>

                {/* SHIPPER / NOTIFY */}

                <tr className="b-0">
                  <td colSpan="4" className="bl-0 bb-0">
                    <b>SHIPPER</b>
                  </td>



                  {/* <td
    colSpan={notifyParty2 ? 2 : 4}
    className="br-0 bl-0 bb-0"
  >
    <b>NOTIFY PARTY</b>
  </td>


 
    <td colSpan="1" className="br-0 bl-0 bb-0">
      <b>NOTIFY PARTY 2</b>
    </td>
 
</tr> */}


                  <td
                    colSpan={hasNotifyParty2 ? 2 : 4}
                    className="br-0 bl-0 bb-0"
                  >
                    <b>NOTIFY PARTY</b>
                  </td>

                  {hasNotifyParty2 && (
                    <td
                      colSpan="2"
                      className="br-0 bl-0 bb-0"
                    >
                      <b>NOTIFY PARTY 2</b>
                    </td>
                  )}
                </tr>





                <tr className="b-0">
                  <td colSpan="4" className="bl-0 bb-0">
                    {shipperName}
                    <br />
                    {invoice.shipper_address}
                  </td>
                  {/* <td
    colSpan={notifyName ? 2 : 4}
    className="bl-0 bb-0 align-top"
  >
    {notifyName}
  </td>

 

    <td
      colSpan="2"
      className="bl-0 bb-0 br-0 align-top"
    >
      {notifyParty2 || "Not provided"}
    </td> */}
                  {/* <tr> */}
                  <td
                    colSpan={hasNotifyParty2 ? 2 : 4}
                    className="bl-0 bb-0 align-top"
                  >
                    {notifyName || "Not provided"}
                    <br />
                    {notifyAddress}
                  </td>

                  {hasNotifyParty2 && (
                    <td
                      colSpan="2"
                      className="bl-0 bb-0 br-0 align-top"
                    >
                      {notifyParty2}
                      <br />
                      {notifyParty2Address}
                    </td>
                  )}
                  {/* </tr> */}

                </tr>



                {/* JOB / BL */}


                <tr className="b-0">
                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>JOB NUMBER</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.job_number}
                  </td>

                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>HBL Number</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0 br-0">
                    {invoice.bl_number}
                  </td>
                </tr>

                {/* PORT */}

                <tr className="b-0">


                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>PLACE OF RECEIPT</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.place_of_receipt}
                  </td>

                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>PORT OF LOADING</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.pol}
                  </td>


                </tr>

                {/* RECEIPT / DELIVERY */}

                <tr className="b-0">



                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>PORT OF DISCHARGE</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.pod}
                  </td>

                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>FINAL PORT OF DELIVERY</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0 br-0">
                    {invoice.final_delivery}
                  </td>
                </tr>

                {/* SEGMENT */}

                <tr className="b-0">
                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>SHIPMENT SEGMENT</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.shipment_segment}
                  </td>
                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>SHIPMENT INCOTERMS</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.incoterms}
                  </td>
                </tr>

                {/* INCOTERMS */}

                <tr className="b-0">


                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>COMMODITY NAME</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.commodity_name}
                  </td>
                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>Vessel Name</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.vessel_name}
                  </td>


                </tr>

                <tr className="b-0">
                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>Voyage Number</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0 ">
                    {invoice.voyage_number}
                  </td>
                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>FREIGHT TERMS</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.freight_terms}
                  </td>





                </tr>


                <tr className="b-0">
                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>Credit Days Actual</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.credit_days || ""}
                  </td>

                  <td colSpan="2" className="bl-0 bb-0">
                    <strong>Credit Days As per System</strong>
                  </td>

                  <td colSpan="2" className="bb-0 bl-0">
                    {invoice.credit_days_system || ""}
                  </td>
                </tr>





                <tr className="b-0">

                  {/* FREIGHT */}
                  {["FE", "FI"].includes(shipmentSegment) && (
                    <>
                      <td colSpan="2" className="bl-0 bb-0">
                        <strong>Gross Weight</strong>
                      </td>

                      <td colSpan="2" className="bb-0 bl-0">
                        {grossWeight}
                      </td>
                    </>
                  )}
                  {["LE", "LI"].includes(shipmentSegment) && (
                    <>
                      <td colSpan="2" className="bl-0 bb-0">
                        <strong>CBM</strong>
                      </td>

                      <td colSpan="2" className="bb-0 bl-0 br-0 ">
                        {invoice.cbm}
                      </td>
                    </>
                  )}
                  {(invoice.shipment_segment === "FE" || invoice.shipment_segment === "FI") && (
                    <>
                      <td colSpan="2" className="bl-0 bb-0">
                        <strong>CONTAINER TYPE</strong>
                      </td>
                      <td colSpan="2" className="bb-0 bl-0 br-0">
                        {containerText}
                      </td>
                    </>
                  )}
                </tr>
                <tr className="b-0">
                  <td colSpan="2" className="bl-0 bb-0 br-0 text-start">
                    <strong>Routing</strong>
                  </td>
                  <td colSpan={invoice?.routing === "Both" || invoice?.routing === "Indirect" ? "2" : "6"}
                    className="br-0 bb-0 text-start">
                    {invoice.routing}
                  </td>
                  {invoice?.routing === "Both" || invoice?.routing === "Indirect" &&
                    <>
                      <td colSpan="2" className=" bb-0 br-0 text-start">
                        <strong>TransShipment Hub</strong>
                      </td>
                      <td colSpan="2" className="bb-0 br-0 text-start">
                        {invoice.transshipment_hub}
                      </td>
                    </>
                  }
                </tr>
                {/* REFERENCE */}

                <tr className="b-0">
                  <td colSpan="8" className="bl-0 bb-0 br-0 text-start">
                    <strong>Reference :</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            <div id="charges-block" className="charges-block">
              <table className="table table-bordered invoice-table custom-invoice-table mb-0 table-layout-fixed bt-0">
                <tbody>
                  {/* CHARGES HEADER */}

                  <tr className="b-0">
                    <th className="fw-bold bb-0 bl-0" style={{width:"18%"}}>CHARGE CODE</th>
                    <th className="fw-bold bb-0 bl-0" style={{width:"8%"}}>SAC</th>
                    <th className="fw-bold bb-0 bl-0" style={{width:"8%"}}>CURR.</th>
                    <th className="fw-bold bb-0 bl-0" style={{width:"8%"}}>RATE</th>
                    <th className="fw-bold bb-0 bl-0" style={{width:"8%"}}>QTY</th>
                    <th className="fw-bold bb-0 bl-0" style={{width:"12%"}}>AMOUNT</th>
                    <th className="fw-bold bb-0 bl-0" style={{width:"12%"}}>AMOUNT (INR)</th>
                    {!isINR && <th className="fw-bold bb-0 bl-0" style={{width:"12%"}}>AMOUNT ({activeCurrency})</th>}
                    {isINR && <th className="fw-bold bb-0 bl-0" style={{width:"6%"}}>GST</th>}
                    {isINR && <th className="fw-bold bb-0 bl-0" style={{width:"6%"}}>GST %</th>}
                    {isINR && <th className="fw-bold bb-0 bl-0 br-0" style={{width:"8%"}}>GST AMT</th>}
                  </tr>

                  {/* CHARGES */}

                  {activeCharges.length > 0 ? (
                    activeCharges.map((charge, index) => {
                      const currency = charge.currency || charge.charge_currency || "INR";
                      const amount = numberValue(charge.amount);
                      const amountInInr = numberValue(charge.amount_in_inr);
                      const sac = charge.sac_code || "";
                      const gstPct = numberValue(charge.gst_percentage);
                      const gstAmt = numberValue(charge.gst_amount);
                      const gstEnabled =
                        labelValue(charge.gst_option) === "yes" || gstPct > 0;
                      const gstLabel = gstEnabled ? `${gstPct}%` : "No";
                      // Amount in invoice currency (for non-INR invoices)
                      const amountInInvoiceCurrency = !isINR
                        ? activeExchangeRate > 0
                          ? (amountInInr / activeExchangeRate).toFixed(2)
                          : "0.00"
                        : null;

                      return (
                        <tr className="b-0" key={charge.charge_id || index}>
                          <td className="bb-0 bl-0">{chargeCodeLabel(charge)}</td>
                          <td className="bb-0 bl-0">{sac}</td>
                          <td className="bb-0 bl-0">{currency}</td>
                          <td className="bb-0 bl-0">{formatNumber(charge.rate)}</td>
                          <td className="bb-0 bl-0">{formatNumber(charge.quantity)}</td>
                          <td className="bb-0 bl-0">{currencySymbol(currency)}{formatNumber(amount)}</td>
                          <td className="bb-0 bl-0">{formatNumber(amountInInr)}</td>
                          {!isINR && <td className="bb-0 bl-0">{currencySymbol(activeCurrency)}{formatNumber(amountInInvoiceCurrency)}</td>}
                          {isINR && <td className="bb-0 bl-0">{gstEnabled ? "Yes" : "No"}</td>}
                          {isINR && <td className="bb-0 bl-0">{gstEnabled ? formatNumber(gstPct) : "0"}</td>}
                          {isINR && <td className="bb-0 bl-0">{gstEnabled ? formatNumber(gstAmt) : "0"}</td>}
                          {!isINR && <td className="bb-0 bl-0 br-0"></td>}
                        </tr>
                      );
                    })
                  ) : (
                    <tr className="b-0">
                      <td
                        className="bb-0 bl-0 br-0 text-center"
                        colSpan={isINR ? 11 : 8}
                      >
                        No charges
                      </td>
                    </tr>
                  )}

                  {/* TOTAL */}

                </tbody>
              </table>
            </div>

            {/* Keep SUB TOTAL through TERMS together on one PDF page */}
            <div className="pdf-keep-together">
              <table className="table table-bordered invoice-table custom-invoice-table bt-0 table-layout-fixed mb-0">
                <tbody>
                  <tr className="b-0">
                    <td colSpan="4" className="fw-bold text-center bb-0 bl-0 br-0">
                      SUB TOTAL
                    </td>

                    <td className="text-center fw-bold bb-0 br-0 bt-0">
                      {currencySym}{formatNumber(subTotal)}
                    </td>

                    {!isINR && activeExchangeRate > 0 && (
                      <td colSpan="3" className="fw-bold br-0 bt-0">
                        <div className="d-flex flex-column align-items-center justify-content-center">
                          <span className="fw-bold">EXCHANGE RATE</span>
                          <span className="fw-bold small">1 {activeCurrency} = ₹ {formatNumber(activeExchangeRate)}</span>
                        </div>
                      </td>
                    )}
                    {isINR && chargeExchangeRates.length > 0 ? (
                      <td colSpan="3" className="fw-bold br-0 bt-0">
                        <div className="d-flex flex-column align-items-center justify-content-center">
                          <span className="fw-bold">EXCHANGE RATES</span>
                          {chargeExchangeRates.map(([curr, rate]) => (
                            <span key={curr} className="fw-bold small">
                              1 {curr} = ₹ {formatNumber(rate)}
                            </span>
                          ))}
                        </div>
                      </td>
                    ) : isINR ? (
                      <td colSpan="3" className="br-0 bt-0"></td>
                    ) : null}
                  </tr>
                  {isINR && (
                    <tr className="b-0">
                      <td colSpan="4" className="fw-bold text-center bb-0 bl-0 br-0">
                        {isInterState ? "IGST" : "CGST / SGST"}
                      </td>
                      <td className="fw-bold text-center bb-0">{currencySym}{formatNumber(gstTotal)}</td>
                      {chargeExchangeRates.length > 0 ? (
                        <td className="fw-bold text-center bb-0 br-0" rowSpan="3" colSpan="3"></td>
                      ) : (
                        <td colSpan="3" className="bb-0 br-0"></td>
                      )}
                    </tr>
                  )}

                  <tr className="b-0">
                    <td colSpan="4" className="fw-bold text-center bb-0 bl-0 br-0">
                      ROUND OFF
                    </td>
                    <td className="fw-bold text-center bb-0 br-0">({currencySym}{formatNumber(roundOff)})</td>
                    {!isINR && <td className="fw-bold text-center bb-0 br-0" rowSpan="2" colSpan="3"></td>}
                    {isINR && chargeExchangeRates.length === 0 && (
                      <td className="fw-bold text-center bb-0 br-0" rowSpan="3" colSpan="3"></td>
                    )}
                  </tr>

                  <tr className="b-0">
                    <td className="fw-bold text-center bb-0 bl-0 br-0" colSpan="4">
                      GRAND TOTAL
                    </td>
                    <td className="text-center fw-bold bb-0 br-0">{currencySym}{formatNumber(grandTotal)}</td>
                  </tr>

                  <tr className="b-0">
                    <td colSpan={isINR ? 8 : 5} className="fw-bold bb-0 br-0 bl-0 finaltotal text-start">
                      {amountWords}
                    </td>
                  </tr>
                </tbody>
              </table>


              <table className="table table-bordered invoice-table custom-invoice-table bt-0 bb-0">
                <tbody>
                  <tr className="b-0">
                    <td className="text-center fw-bold b-0 br fsbg bt-0" colSpan="4">
                      TERMS & CONDITIONS
                    </td>
                    <td className="text-center fw-bold b-0 fsbg bt-0" colSpan="4">
                      Bank Details
                    </td>
                  </tr>
                  <tr className="bb-0">
                    <td colSpan="4" className="br-0 bl-0 text-start">
                      <ol style={{ paddingLeft: "12px" }}>
                        {termsandcondition.map((term, index) => (
                          <li key={index}>{term}</li>
                        ))}
                      </ol>
                    </td>
                    <td colSpan="4" className="br-0 text-start">
                      <div className="ps-1 text-start">

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px", flexShrink: 0 }}
                          >
                            ACCOUNT NAME :
                          </p>

                          <p className="mb-0">
                            GFR Logistics India Private Limited
                          </p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            BANK NAME :
                          </p>

                          <p className="mb-0">Yes Bank (India)</p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            ACCOUNT NUMBER :
                          </p>

                          <p className="mb-0">10576370000052</p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            IFSC :
                          </p>

                          <p className="mb-0">YESB0000107</p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            SWIFT :
                          </p>

                          <p className="mb-0">YESBINBB</p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            BANK ADDRESS :
                          </p>

                          <p className="mb-0">
                            GF, SCO No.3, SECTOR 20 D, Chandigarh-160020
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* BANK DETAILS */}
            <table className="table table-bordered invoice-table custom-invoice-table bt-0 bb-0">
              <tbody>
                {/* <tr className="b-0">
                  <td colSpan="6" className="b-0 py-0 bt-0">
                    <div className="d-flex">
                      <div className="br pe-3 me-3 text-start">
                        <div className="fw-bold b-0 fs-6">Bank Details</div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px", flexShrink: 0 }}
                          >
                            ACCOUNT NAME :
                          </p>

                          <p className="mb-0">
                            GFR Logistics India Private Limited
                          </p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            BANK NAME :
                          </p>

                          <p className="mb-0">Yes Bank (India)</p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            ACCOUNT NUMBER :
                          </p>

                          <p className="mb-0">10576370000052</p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            IFSC :
                          </p>

                          <p className="mb-0">YESB0000107</p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            SWIFT :
                          </p>

                          <p className="mb-0">YESBINBB</p>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                          <p
                            className="fw-bold mb-0"
                            style={{ minWidth: "150px" }}
                          >
                            BANK ADDRESS :
                          </p>

                          <p className="mb-0">
                            GF, SCO No.3, SECTOR 20 D, Chandigarh-160020
                          </p>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr> */}

                {/* REGISTERED ADDRESS */}

                <tr className="b-0">
                  <td colSpan="8" className="br-0 bl-0 bb-0">
                    <div className="d-flex align-items-center gap-4">
                      <p className="fw-bold mb-0" style={{ minWidth: "150px" }}>
                        Registered Address :
                      </p>

                      <p className="mb-0">
                        Office No. 416, 4th Floor, Vishwa Deep Tower, District
                        Centre, Janakpuri, New Delhi, India - 110058. CIN:
                        U50120DL2023PTC411446
                      </p>
                    </div>
                  </td>
                </tr>

              </tbody>
            </table>

            {/* Computer-generated note — moved after the charge table in the PDF */}
            <table
              id="computer-generated-note"
              className="table table-bordered invoice-table custom-invoice-table bt-0 bb-0"
            >
              <tbody>
                <tr className="b-0">
                  <td colSpan="8" className="text-center br-0 bl-0 bb-0">
                    <b>This is a computer generated invoice</b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div >

        {/* Confirmation Modal */}
        {
          showConfirm && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
              tabIndex="-1"
              role="dialog"
            >
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {isEditing
                        ? "Confirm Update Performa Invoice"
                        : "Confirm Create Performa Invoice"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowConfirm(false)}
                      disabled={saving}
                    ></button>
                  </div>
                  <div className="modal-body">
                    {saving ? (
                      <>
                        <div className="d-flex align-items-center">
                          <span
                            className="spinner-border text-primary me-3"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          <span>
                            {isEditing
                              ? "Updating performa invoice and regenerating PDF. Please wait..."
                              : "Creating performa invoice and generating PDF. Please wait..."}
                          </span>
                        </div>
                        <span className="visually-hidden" role="status">
                          {isEditing
                            ? "Updating performa invoice, please wait"
                            : "Creating performa invoice, please wait"}
                        </span>
                      </>
                    ) : (
                      <>
                        <p>
                          {isEditing
                            ? "Are you sure you want to update the existing performa invoice?"
                            : "Are you sure you want to create the performa invoice?"}
                        </p>
                        <p className="mb-0 text-muted small">
                          {isEditing
                            ? "This will replace the saved invoice details and regenerate the PDF."
                            : "This will save the invoice details to the database and generate a PDF."}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowConfirm(false)}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCreatePerforma}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : isEditing ? (
                        "Update"
                      ) : (
                        "Confirm"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }
        {/* Success Modal */}
        {
          showSuccessModal && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
              tabIndex="-1"
              role="dialog"
            >
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {isEditing
                        ? "Performa Invoice Updated"
                        : "Performa Invoice Created"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={handleCloseSuccessModal}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <p className="mb-0">
                      {isEditing
                        ? "Performa invoice updated and PDF regenerated successfully."
                        : "Performa invoice created and PDF generated successfully."}
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSuccessOk}
                    >
                      Okay
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }

      </div >
    </>
  );

};

export default CreatePerformaInvoice;
